import React, { useState, useEffect } from 'react';
import { citiesApi } from '../../api/cities.js';

function CityModal({ city, onClose, onSave }) {
  const [name, setName] = useState(city?.name || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    if (!name.trim()) { setErr('City name is required.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({ name: name.trim() });
    } catch (e) {
      setErr(e.message || 'Save failed.');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 420, maxWidth: '90vw' }}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>{city ? 'Edit City' : 'Add City'}</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && (
            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>
              {err}
            </div>
          )}
          <div className="form-group">
            <label className="form-label">City Name *</label>
            <input
              className="form-control"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Kolkata"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

export default function CitiesPage() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await citiesApi.getAll();
      setCities(res.data || []);
    } catch (e) {
      console.error('Failed to load cities:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (payload) => {
    if (editItem) {
      await citiesApi.update(editItem.id, payload);
    } else {
      await citiesApi.create(payload);
    }
    setShowModal(false);
    setEditItem(null);
    await load();
  };

  const handleToggleActive = async (city) => {
    try {
      await citiesApi.update(city.id, { is_active: !city.is_active });
      await load();
    } catch (e) { alert(e.message || 'Update failed.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this city? This cannot be undone.')) return;
    try {
      await citiesApi.delete(id);
      await load();
    } catch (e) { alert(e.message || 'Delete failed.'); }
  };

  return (
    <div className="page-body">
      {showModal && (
        <CityModal
          city={editItem}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSave={handleSave}
        />
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span className="card-title">Cities</span>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              Manage cities — top level of the location hierarchy
            </p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ Add City</button>
        </div>

        <div className="card-body">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>City Name</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)' }}>Loading…</td></tr>
                ) : cities.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)' }}>No cities yet</td></tr>
                ) : (
                  cities.map((c, i) => (
                    <tr key={c.id}>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td>
                        <span
                          className={`badge badge-${c.is_active ? 'success' : 'secondary'}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleToggleActive(c)}
                          title="Click to toggle"
                        >
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                          <button
                            className="btn btn-outline btn-xs"
                            onClick={() => { setEditItem(c); setShowModal(true); }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-outline btn-xs"
                            style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                            onClick={() => handleDelete(c.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
